"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

// The rooms rail stays put while the threads scroll, and scrolls on its own
// when it is taller than the viewport (20 rooms is taller than most laptops).
//
// `position: sticky` cannot do this here: the app shell's <main> has
// overflow-x-auto and its wrapper overflow-x-hidden, which makes both of them
// scroll containers and defeats sticky for everything inside. So the rail is
// pinned with `fixed`, measured off its own placeholder column every frame the
// page scrolls or resizes (sidebar collapse included), and kept clear of the
// shell's sticky header.
const GAP = 12;

export function StickyRail({ children }: { children: ReactNode }) {
  const placeholder = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ left: number; top: number; width: number; maxHeight: number } | null>(null);

  useEffect(() => {
    const el = placeholder.current;
    if (!el) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      if (r.width === 0) {
        setBox(null); // rail column hidden (narrow screens)
        return;
      }
      // Floor = just under the shell header while it is on screen, else the
      // viewport top (the header scrolls away with the page in this shell).
      const header = document.querySelector<HTMLElement>("[data-app-header]");
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      const minTop = Math.max(GAP, headerBottom + GAP);
      const top = Math.max(minTop, r.top);
      setBox({ left: r.left, top, width: r.width, maxHeight: Math.max(160, window.innerHeight - top - GAP) });
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    ro.observe(document.body);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={placeholder} className="w-full">
      <div
        style={box ? { position: "fixed", left: box.left, top: box.top, width: box.width, maxHeight: box.maxHeight, overflowY: "auto" } : undefined}
        className="[scrollbar-width:thin] pr-1"
      >
        {children}
      </div>
    </div>
  );
}
